import { v4 as uuid } from 'uuid';
import emailSdk from '../services/email-sdk';
import { Intent, Position, Toaster } from '@blueprintjs/core';
import { parseStringTemplate } from 'string-template-parser';

const AppToaster = Toaster.create( {
    position: Position.TOP
} );

/*******************************************************************************
 * Initial state
 */
const initialState = {
    emails: [],
    email: '',
    preview: '',
    emailsLoading: false,
    emailsLoaded: false
};

/*******************************************************************************
 * Non-async action types
 */
export const types = {
    setEmails: 'email-input/setEmails',
    setEmail: 'email-input/setEmail',
    setPreview: 'email-input/setPreview',
    setEmailsLoading: 'email-input/setEmails/loading',
    setEmailsLoaded: 'email-input/setEmails/loaded'
};

/*******************************************************************************
 * Reducer
 */
export default function emailReducer( state = initialState, action ) {
    switch ( action.type ) {
        case types.setEmails:
            return {
                ...state,
                emails: action.emails,
                emailsLoading: false,
                emailsLoaded: true
            };
        case types.setEmail:
            return {
                ...state,
                email: action.email,
                emailsLoading: false,
                emailsLoaded: true
            };
        case types.setPreview:
            return {
                ...state,
                preview: action.preview.preview
            };
        default:
            return state;
    }
}

/*******************************************************************************
 * Action creators
 */
export const actions = {
    loadEmails: () => {
        return ( dispatch ) => {
            dispatch( actions.setEmailsLoading( true ) );

            return emailSdk.getEmails()
                .then( ( emails ) => {
                    dispatch( actions.setEmails( emails ) );
                    return emails;
                } )
                .catch( ( err ) => {
                    dispatch( actions.setEmails( [] ) );
                    return [];
                } );
        };
    },

    setEmails: ( emails ) => {
        return {
            type: types.setEmails,
            emails
        };
    },

    setEmail: ( email ) => {
        return {
            type: types.setEmail,
            email
        };
    },

    setPreview: ( preview ) => {
        return {
            type: types.setPreview,
            preview
        };
    },

    setEmailsLoading: ( loading ) => {
        return {
            type: types.setEmailsLoading,
            loading
        };
    },

    setEmailsLoaded: ( loaded ) => {
        return {
            type: types.setEmailsLoaded,
            loaded
        };
    },


    setEmailInputs: ( emailId, locale, inputs ) => {
        return {
            type: types.setEmailInputs,
            emailId,
            locale,
            inputs
        };
    },

    setEmailInputsLoading: ( emailId, locale, loading ) => {
        return {
            type: types.setEmailInputsLoading,
            emailId,
            locale,
            loading
        };
    },

    setEmailInputsLoaded: ( emailId, locale, loaded ) => {
        return {
            type: types.setEmailInputsLoaded,
            emailId,
            locale,
            loaded
        };
    },
    fetchPreview: ( inputsCopy ) => {
        return ( dispatch, getState ) => {
            const state = getState();

            const previewRequest = {
                layout: state.email.email.layout,
                product: state.email.email.product,
                name: state.email.email.name,
                type: state.email.email.type,
                components: state.email.email.components.map( component => {
                    return {
                        name: component.name,
                        namespace: component.namespace,
                        props: Object.keys( component.props ).reduce( ( flatProps, propKey ) => {
                            flatProps[ propKey ] = component.props[ propKey ].objectPath;
                            return flatProps;
                        }, {} )
                    };
                } ),
                inputs: Object.values( inputsCopy )[ 0 ].reduce( ( flatInputs, current ) => {

                    const logo = state.company.activeCompany.primaryLogo;
                    const color = state.company.activeCompany.primaryColor;
                    if ( typeof current.value === 'object' ) {
                        flatInputs[ current.code ] = current.value;
                    }
                    else {
                        flatInputs[ current.code ] = current.value && current.value.template( {
                            companyName: '${companyName}',
                            primaryColor: color,
                            primaryLogo: logo,
                            giftCodeUrl: '${giftCodeUrl}',
                            referenceNumber: '${referenceNumber}',
                            programName: '${programName}',
                            expirationDate: '${expirationDate}',
                            firstName: '${firstName}',
                            milestoneName: '${milestoneName}',
                            emailImageUrl: '${emailImageUrl}',
                            giftCodeProduct1Url: '${giftCodeProduct1Url}',
                            giftCodeProduct2Url: '${giftCodeProduct2Url}',
                            giftCodeProduct3Url: '${giftCodeProduct3Url}',
                            celebrationUrl: '${celebrationUrl}'
                        } );
                    }

                    return flatInputs;
                }, {} ),
                rawContentStates: Object.values( inputsCopy )[ 0 ].reduce( ( flatRawContentStates, current ) => {
                    if ( current.rawContentState && Object.keys( current.rawContentState ).length > 0 ) {
                        flatRawContentStates[ current.code ] = current.rawContentState;
                    }

                    return flatRawContentStates;
                }, {} )
            };

            return emailSdk.fetchPreview( previewRequest )
                .then( ( response ) => {
                    dispatch( actions.setPreview( response ) );
                    return response;
                } )
                .catch( ( err ) => {
                    console.log( err );
                } );
        };
    },
    sendForTranslations: ( customEmailId, inputsCopy, locales ) => {
        return ( dispatch, getState ) => {
            const state = getState();

            const updatedEmailBody = {
                locale: 'en-US',
                ...state.email.email,
                id: customEmailId, // Replace id with customEmailId as we are sending translations for custom emails not the gold copy email.
                inputs: inputsCopy[ 'en-US' ]
            };

            updatedEmailBody.inputs = updatedEmailBody.inputs.map( input => {
                return {
                    ...input,
                    id: uuid()
                };
            } );

            updatedEmailBody.components = updatedEmailBody.components.map( comp => {
                return {
                    ...comp,
                    id: uuid(),
                    props: Object.keys( comp.props ).reduce( ( newProps, cur ) => {
                        newProps[ cur ] = {
                            required: comp.props[ cur ].required,
                            type: comp.props[ cur ].type,
                            name: comp.name
                        };

                        let objectPath = null;
                        if ( !comp.props[ cur ].objectPath ) {
                            updatedEmailBody.inputs.push( {
                                code: `${ parseStringTemplate( objectPath ).variables[ 0 ].name.split( ':' )[ 1 ].split( '.' )[ 1 ] }`,
                                id: uuid(),
                                required: comp.props[ cur ].required,
                                toOverride: comp.props[ cur ].toOverride || false,
                                toTranslate: comp.props[ cur ].toTranslate || false,
                                allowRichText: comp.props[ cur ].allowRichText || false,
                                rawContentState: comp.props[ cur ].rawContentState || {},
                                type: comp.props[ cur ].type,
                                value: ''
                            } );
                        } else {
                            objectPath = comp.props[ cur ].objectPath;
                        }

                        const parsedObjectPath = parseStringTemplate( objectPath );
                        const inputCode = parsedObjectPath.variables[ 0 ].name.split( ':' )[ 1 ].split( '.' )[ 1 ];
                        const emailInput = updatedEmailBody.inputs.find( input => input.code === inputCode );
                        if ( emailInput ) {
                            newProps[ cur ].inputId = emailInput.id;
                        } else {
                            newProps[ cur ].inputId = uuid();
                        }
                        return newProps;

                    }, {} )
                };
            } );

            const customOptions = [ {
               locales: locales.map( locale => {
                    return {
                        value: locale.code,
                        label: locale.label
                    };
               } )
            } ];

            return emailSdk.translate( updatedEmailBody, customOptions )
                .then( ( response ) => {
                    AppToaster.show( {
                        message: `Successfully sent ${ state.email.email.name } to translations.`,
                        intent: Intent.SUCCESS,
                        icon: 'tick',
                        timeout: 5000
                    } );
                    return response;
                } )
                .catch( ( err ) => {
                    console.log( err );
                    AppToaster.show( {
                        message: err,
                        intent: Intent.DANGER,
                        icon: 'warning-sign',
                        timeout: 0
                    } );
                } );
        };
    },
    fetchEmail: ( ( emailId ) => {

        return ( dispatch ) => {
            dispatch( actions.setEmailsLoading( true ) );

            return emailSdk.fetchEmail( emailId )
                .then( ( email ) => {
                    dispatch( actions.setEmail( email ) );
                    return email;
                } )
                .catch( ( err ) => {
                    console.log( err );
                } );
        };
    } )
};
